<template>
  <div
    class="base-radar-view-lite"
    :class="{ 'is-fill-container': fillContainer, 'is-region-viewport': viewport === 'region' }"
    :style="containerStyle"
    ref="rootRef"
  >
    <canvas ref="canvasRef" :width="actualSize.width" :height="actualSize.height"></canvas>

    <div class="overlay-layer">
      <!-- 目标点 -->
      <slot name="targets">
        <div
          v-for="target in targets || []"
          :key="target.id"
          class="target-point"
          :style="getTargetStyle(target)"
        >
          <span class="target-id">{{ target.id }}</span>
        </div>
      </slot>

      <!-- 家具展示（只读，排除 radar：canvas 已绘制雷达图标） -->
      <div class="furniture-layer">
        <div
          v-for="item in displayFurnitures"
          :key="item.name + item.type"
          class="furniture-item"
          :style="getFurnitureStyle(item)"
        >
          <furnitureIcon :icon="item.type" :width="item.width * displayScale" :height="item.length * displayScale" :draggable="false" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch, computed, nextTick, type CSSProperties } from 'vue'
import radarUrl from '@/assets/furnitures/radar.png'
import { convert_region_r2c, rotateRect_cw, getDetectionRegionBoundsInBase } from '@/utils/coordTransform'
import type { TargetPoint } from '@/types/radar'
import type { FurnitureItem } from '@/api/room/types'
import * as roomApi from '@/api/room'
import furnitureIcon from '@/components/furnitureIcon/index.vue'

defineOptions({ name: 'BaseRadarViewLite' })

// ==================== 基准配置（500px 画布下的原始尺寸） ====================
const BASE_CONFIG = {
  canvasSize: 500,
  gridSpacing: 50,
  radarSize: 20,
  lineWidth: 0.5,
  axisLineWidth: 1,
  fontSize: 12,
  targetSize: 18,
  detectionAreaBorder: 1,
} as const

export type RadarViewport = 'full' | 'region'

interface Props {
  devId: string // 设备 ID（用于获取家具）
  coordinates: number[] // 检测区域坐标
  angle?: number // 旋转角度，默认 0 度
  targets?: TargetPoint[] // 目标点数组
  adaptive?: boolean // 是否自适应容器尺寸
  minSize?: number // 最小尺寸
  maxSize?: number // 最大尺寸
  /** 视口模式：full=500 画布全览，region=仅展示检测区域 */
  viewport?: RadarViewport
  // 可选：直接传入家具数据（优先级高于 devId 接口拉取）
  furnitures?: FurnitureItem[]
  /** 为 true 时仅使用 props.furnitures，不再请求 room 接口 */
  furnitureFromParent?: boolean
  /** 嵌入户型房间时：画布严格适配容器，不使用 minSize 撑大 */
  fillContainer?: boolean
  /** 父级布局变化时递增，用于触发重绘（如画布缩放） */
  layoutVersion?: number
  /** region 视口检测区域填充色，默认淡蓝 */
  regionFillColor?: string
}

const DEFAULT_REGION_FILL = 'rgba(52, 152, 219, 0.3)'

const props = withDefaults(defineProps<Props>(), {
  angle: 0,
  adaptive: true,
  minSize: 200,
  maxSize: 800,
  viewport: 'full',
  furnitures: () => [],
  furnitureFromParent: false,
  fillContainer: false,
  layoutVersion: 0,
  regionFillColor: DEFAULT_REGION_FILL,
})

const canvasRef = ref<HTMLCanvasElement | null>(null)
const rootRef = ref<HTMLElement | null>(null)
let ctx: CanvasRenderingContext2D | null = null

// 缓存雷达图片（模块级预加载，避免异步回调重复绘制）
const cachedRadarImage: HTMLImageElement = new Image()
cachedRadarImage.src = radarUrl

/** 过滤家具列表中的 radar（canvas 中心已绘制雷达） */
const stripRadarFurniture = (items: FurnitureItem[]) =>
  items.filter((item) => String(item.type).toLowerCase() !== 'radar')

// 本地家具数据（每个实例独立）
const localFurnitures = ref<FurnitureItem[]>([])

const displayFurnitures = computed(() => localFurnitures.value)

const isRegionViewport = computed(() => props.viewport === 'region')

const regionBoundsBase = computed(() =>
  getDetectionRegionBoundsInBase(props.coordinates, props.angle, BASE_CONFIG.canvasSize)
)

/** 当前视口在基准画布坐标系下的宽高（cm≈px） */
const logicalViewport = computed(() => {
  if (isRegionViewport.value) {
    const r = regionBoundsBase.value
    return {
      width: Math.max(r.width, 1),
      height: Math.max(r.height, 1),
    }
  }
  return {
    width: BASE_CONFIG.canvasSize,
    height: BASE_CONFIG.canvasSize,
  }
})

/** region 模式下视口相对 500 画布的偏移 */
const viewportOffsetBase = computed(() => {
  if (!isRegionViewport.value) {
    return { x: 0, y: 0 }
  }
  const r = regionBoundsBase.value
  return { x: r.left, y: r.top }
})

// ==================== 自适应尺寸计算 ====================
const actualSize = ref({ width: BASE_CONFIG.canvasSize, height: BASE_CONFIG.canvasSize })

/** 显示像素 / 逻辑视口宽 */
const displayScale = computed(() => {
  const lw = logicalViewport.value.width
  if (actualSize.value.width <= 0 || lw <= 0) return 1
  return actualSize.value.width / lw
})

const adaptiveConfig = computed(() => ({
  gridSpacing: BASE_CONFIG.gridSpacing * displayScale.value,
  radarSize: BASE_CONFIG.radarSize * displayScale.value,
  lineWidth: BASE_CONFIG.lineWidth * displayScale.value,
  axisLineWidth: BASE_CONFIG.axisLineWidth * displayScale.value,
  fontSize: BASE_CONFIG.fontSize * displayScale.value,
  targetSize: BASE_CONFIG.targetSize * displayScale.value,
  detectionAreaBorder: BASE_CONFIG.detectionAreaBorder * displayScale.value,
}))

const containerStyle = computed(() => ({
  width: '100%',
  height: '100%',
}))

const fitSizeInContainer = (
  containerWidth: number,
  containerHeight: number,
  contentWidth: number,
  contentHeight: number
) => {
  const aspect = contentWidth / contentHeight
  const cw = containerWidth
  const ch = containerHeight

  if (cw / ch > aspect) {
    return { width: ch * aspect, height: ch }
  }
  return { width: cw, height: cw / aspect }
}

const computeSize = () => {
  if (!rootRef.value) {
    return {
      width: logicalViewport.value.width,
      height: logicalViewport.value.height,
    }
  }

  const containerWidth = rootRef.value.clientWidth
  const containerHeight = rootRef.value.clientHeight

  if (containerWidth <= 0 || containerHeight <= 0) {
    return { width: 0, height: 0 }
  }

  const lw = logicalViewport.value.width
  const lh = logicalViewport.value.height

  if (props.fillContainer) {
    const fitted = fitSizeInContainer(containerWidth, containerHeight, lw, lh)
    return {
      width: Math.min(Math.max(fitted.width, 1), props.maxSize),
      height: Math.min(Math.max(fitted.height, 1), props.maxSize),
    }
  }

  if (isRegionViewport.value) {
    const fitted = fitSizeInContainer(
      Math.max(containerWidth, props.minSize),
      Math.max(containerHeight, props.minSize),
      lw,
      lh
    )
    return {
      width: Math.min(fitted.width, props.maxSize),
      height: Math.min(fitted.height, props.maxSize),
    }
  }

  const size = Math.min(
    Math.max(containerWidth, props.minSize),
    Math.max(containerHeight, props.minSize),
    props.maxSize
  )

  return {
    width: size,
    height: size,
  }
}

let resizeObserver: ResizeObserver | null = null

const refreshLayout = () => {
  nextTick(() => {
    if (!rootRef.value) return
    actualSize.value = computeSize()
    drawCanvas()
  })
}

const setupResizeObserver = () => {
  if (!rootRef.value) return

  resizeObserver = new ResizeObserver(() => {
    refreshLayout()
  })
  resizeObserver.observe(rootRef.value)
  actualSize.value = computeSize()
}

const standardRadarPosition = () => ({
  x_radar: BASE_CONFIG.canvasSize / 2,
  y_radar: BASE_CONFIG.canvasSize / 2,
})

/**
 * 绘制画布
 */
const drawCanvas = () => {
  if (!canvasRef.value) return
  ctx = canvasRef.value.getContext('2d')
  if (!ctx) return

  const CANVAS_W = actualSize.value.width
  const CANVAS_H = actualSize.value.height

  if (CANVAS_W < 1 || CANVAS_H < 1) return

  const dpr = window.devicePixelRatio || 1
  const pixelW = Math.round(CANVAS_W * dpr)
  const pixelH = Math.round(CANVAS_H * dpr)

  if (canvasRef.value.width !== pixelW || canvasRef.value.height !== pixelH) {
    canvasRef.value.width = pixelW
    canvasRef.value.height = pixelH
    ctx = canvasRef.value.getContext('2d')
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  if (!ctx) {
    ctx = canvasRef.value.getContext('2d')
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  const scale = displayScale.value
  const offset = viewportOffsetBase.value

  ctx.clearRect(0, 0, CANVAS_W, CANVAS_H)
  ctx.fillStyle = isRegionViewport.value ? props.regionFillColor : '#f2f2f0'
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H)

  ctx.save()
  ctx.translate(-offset.x * scale, -offset.y * scale)
  ctx.scale(scale, scale)

  const baseW = BASE_CONFIG.canvasSize
  const baseH = BASE_CONFIG.canvasSize
  const radarPosition = standardRadarPosition()

  drawGrid(ctx, { width: baseW, height: baseH })
  drawCoordinateAxes(ctx, { width: baseW, height: baseH, angle: props.angle })

  if (!isRegionViewport.value) {
    drawDetectionArea(ctx, {
      area: props.coordinates,
      radarPosition,
      angle: props.angle,
    })
  }

  if (cachedRadarImage.complete) {
    drawRadarImage(ctx, {
      width: baseW,
      height: baseH,
      radarImage: cachedRadarImage,
      angle: props.angle,
    })
  }

  ctx.restore()
}

/**
 * 绘制背景网格
 */
const drawGrid = (ctx: CanvasRenderingContext2D, options: { width: number; height: number }) => {
  const { width, height } = options
  const spacing = BASE_CONFIG.gridSpacing
  const color = '#d9d9d7'
  const lineWidth = BASE_CONFIG.lineWidth

  ctx.strokeStyle = color
  ctx.lineWidth = lineWidth

  for (let y = spacing; y < height; y += spacing) {
    ctx.beginPath()
    ctx.moveTo(0, y)
    ctx.lineTo(width, y)
    ctx.stroke()
  }

  for (let x = spacing; x < width; x += spacing) {
    ctx.beginPath()
    ctx.moveTo(x, 0)
    ctx.lineTo(x, height)
    ctx.stroke()
  }
}

/**
 * 绘制坐标轴
 */
const drawCoordinateAxes = (
  ctx: CanvasRenderingContext2D,
  options: { width: number; height: number; angle: number }
) => {
  const { width, height, angle } = options
  const centerX = width / 2
  const centerY = height / 2
  const angleRad = (angle * Math.PI) / 180

  ctx.save()
  ctx.translate(centerX, centerY)
  ctx.rotate(angleRad)
  ctx.translate(-centerX, -centerY)

  ctx.lineWidth = BASE_CONFIG.axisLineWidth
  ctx.setLineDash([5, 5])

  ctx.strokeStyle = '#2c3e50'
  ctx.beginPath()
  ctx.moveTo(0, centerY)
  ctx.lineTo(width, centerY)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(centerX, 0)
  ctx.lineTo(centerX, height)
  ctx.stroke()

  ctx.setLineDash([])
  ctx.restore()
}

/**
 * 绘制雷达图
 */
const drawRadarImage = (
  ctx: CanvasRenderingContext2D,
  options: {
    width: number
    height: number
    radarImage: HTMLImageElement
    angle: number
    offset?: number
  }
) => {
  const { width, height, angle, radarImage, offset = 90 } = options
  const size = BASE_CONFIG.radarSize
  const centerX = width / 2
  const centerY = height / 2
  const halfSize = size / 2
  const angleRad = ((angle + offset) * Math.PI) / 180

  ctx.save()
  ctx.translate(centerX, centerY)
  ctx.rotate(angleRad)
  ctx.drawImage(radarImage, -halfSize, -halfSize, size, size)
  ctx.restore()
}

/**
 * 绘制检测区域（基准 500 坐标系，外层 transform 负责缩放/裁剪）
 */
const drawDetectionArea = (
  ctx: CanvasRenderingContext2D,
  options: {
    area: number[]
    radarPosition: { x_radar: number; y_radar: number }
    angle: number
  }
) => {
  const { area, angle } = options
  const [xStart, xEnd, yStart, yEnd] = area

  if (xEnd <= xStart || yEnd <= yStart) return

  const radarPosition = standardRadarPosition()

  const canvasRect = convert_region_r2c(
    {
      x_cm_start: xStart,
      x_cm_stop: xEnd,
      y_cm_start: yStart,
      y_cm_stop: yEnd,
    },
    radarPosition
  )

  const rotatedRect = rotateRect_cw(
    canvasRect,
    { x: radarPosition.x_radar, y: radarPosition.y_radar },
    angle
  )

  ctx.strokeStyle = '#1677ff'
  ctx.lineWidth = BASE_CONFIG.detectionAreaBorder
  ctx.strokeRect(rotatedRect.left, rotatedRect.top, rotatedRect.width, rotatedRect.height)

  ctx.fillStyle = props.regionFillColor
  ctx.fillRect(rotatedRect.left, rotatedRect.top, rotatedRect.width, rotatedRect.height)
}

/** 基准画布坐标 → 视口 overlay 像素坐标 */
const mapBaseToOverlay = (x: number, y: number) => {
  const offset = viewportOffsetBase.value
  const scale = displayScale.value
  return {
    x: (x - offset.x) * scale,
    y: (y - offset.y) * scale,
  }
}

/**
 * 获取家具样式
 */
const getFurnitureStyle = (item: FurnitureItem): CSSProperties => {
  const pos = mapBaseToOverlay(item.left, item.top)
  const scale = displayScale.value

  return {
    position: 'absolute',
    left: `${pos.x}px`,
    top: `${pos.y}px`,
    transform: `rotate(${item.rotate}deg)`,
    transformOrigin: 'center center',
    width: `${item.width * scale}px`,
    height: `${item.length * scale}px`,
    pointerEvents: 'none',
    zIndex: 100,
    cursor: 'default',
  } as CSSProperties
}

/**
 * 获取目标点样式
 */
const getTargetStyle = (target: TargetPoint) => {
  const color = 'red'
  const targetSize = adaptiveConfig.value.targetSize
  const pos = mapBaseToOverlay(target.displayX, target.displayY)

  return {
    position: 'absolute',
    width: `${targetSize}px`,
    height: `${targetSize}px`,
    background: color,
    borderRadius: '50%',
    transform: `translate3d(${pos.x}px, ${pos.y}px, 0) translate(-50%, -50%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: `${12 * displayScale.value}px`,
    fontWeight: '600',
    pointerEvents: 'none',
    zIndex: 1000,
    transition: 'transform 1s linear',
    willChange: 'transform',
    boxShadow: '0 0 8px rgba(0, 0, 0, 0.5)',
    userSelect: 'none',
  } as CSSProperties
}

// 监听传入的家具数据（仅更新 overlay，不重绘 canvas）
watch(
  () => props.furnitures,
  (val) => {
    localFurnitures.value = stripRadarFurniture(val || [])
  },
  { deep: true, immediate: true }
)

// 监听 devId 变化，获取家具数据（父组件已传入时跳过接口）
watch(
  () => props.devId,
  async (newDevId) => {
    if (props.furnitureFromParent) {
      localFurnitures.value = stripRadarFurniture(props.furnitures)
      return
    }

    if (!newDevId) {
      localFurnitures.value = []
      return
    }

    if (props.furnitures.length > 0) {
      localFurnitures.value = stripRadarFurniture(props.furnitures)
      return
    }

    try {
      const res = await roomApi.queryRoomInfo({ devId: newDevId })
      localFurnitures.value = stripRadarFurniture(res.data.furnitures || [])
    } catch (error) {
      console.error('❌ BaseRadarViewLite 获取家具失败', error)
      localFurnitures.value = []
    }
  },
  { immediate: true }
)

watch(
  () => [props.angle, props.viewport, props.regionFillColor, ...props.coordinates].join(','),
  () => {
    refreshLayout()
  }
)

watch(
  () => props.layoutVersion,
  () => {
    refreshLayout()
  }
)

onMounted(() => {
  if (cachedRadarImage.complete) {
    drawCanvas()
  } else {
    cachedRadarImage.addEventListener('load', () => drawCanvas(), { once: true })
  }
  setupResizeObserver()
})

onUnmounted(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})
</script>

<style scoped lang="less">
.base-radar-view-lite {
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;

  canvas {
    border: 2px solid #ddd;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    pointer-events: none;
    width: 100%;
    height: 100%;
  }

  &.is-fill-container canvas {
    border: none;
    box-shadow: none;
  }

  .overlay-layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;

    .target-point {
      position: absolute;
      pointer-events: none;
    }

    .furniture-layer {
      width: 100%;
      height: 100%;
      position: absolute;

      .furniture-item {
        position: absolute;
        pointer-events: none;
      }
    }
  }
}
</style>
